import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { LocalNotifications } from "@capacitor/local-notifications";
import { PushNotifications } from "@capacitor/push-notifications";
import { useUserSession } from "shared/contexts/UserContext";
import { normalizeCapacitorDeepLink } from "lib/deepLink";
import { fetchMobileNotificationSettings, registerMobileDevice, unregisterMobileDevice } from "shared/mobile/api/Mobile";

const DEVICE_ID_KEY = "sihayplata_device_id_v1";
const PUSH_SETTINGS_EVENT = "sihayplata:push-settings-updated";

export function dispatchPushSettingsUpdated(): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event(PUSH_SETTINGS_EVENT));
}

function getOrCreateDeviceId(): string {
    try {
        let id = localStorage.getItem(DEVICE_ID_KEY);
        if (!id) {
            id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `dev-${Date.now()}`;
            localStorage.setItem(DEVICE_ID_KEY, id);
        }
        return id;
    } catch {
        return `dev-${Date.now()}`;
    }
}

/**
 * Deep links (app URL + local notification tap) and FCM registration when masterEnabled.
 */
export function useMobileEffects(): void {
    const navigate = useNavigate();
    const { profile } = useUserSession();

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;
        let subUrl: { remove: () => Promise<void> } | undefined;
        let subLocal: { remove: () => Promise<void> } | undefined;
        void CapacitorApp.addListener("appUrlOpen", ({ url }) => {
            navigate(normalizeCapacitorDeepLink(url));
        }).then((h) => { subUrl = h; });
        void LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
            const raw = action.notification.extra?.deepLink;
            if (typeof raw === "string" && raw) navigate(normalizeCapacitorDeepLink(raw));
        }).then((h) => { subLocal = h; });
        return () => {
            void subUrl?.remove();
            void subLocal?.remove();
        };
    }, [navigate]);

    useEffect(() => {
        if (!Capacitor.isNativePlatform() || !profile.token) return;
        let regListener: { remove: () => Promise<void> } | undefined;
        let tapListener: { remove: () => Promise<void> } | undefined;
        let cancelled = false;

        const runPushSetup = async () => {
            try {
                const settings = await fetchMobileNotificationSettings(profile.token!);
                if (cancelled) return;

                if (!settings.masterEnabled) {
                    try {
                        await PushNotifications.unregister();
                    } catch {
                        /* ignore */
                    }
                    try {
                        await unregisterMobileDevice(profile.token!, getOrCreateDeviceId());
                    } catch {
                        /* ignore */
                    }
                    return;
                }

                const perm = await PushNotifications.requestPermissions();
                if (perm.receive !== "granted") return;
                await PushNotifications.register();
            } catch {
                /* ignore */
            }
        };

        void PushNotifications.addListener("registration", async (tok) => {
            try {
                const settings = await fetchMobileNotificationSettings(profile.token!);
                if (!settings.masterEnabled) return;
                await registerMobileDevice(profile.token!, {
                    platform: Capacitor.getPlatform() === "ios" ? "ios" : "android",
                    pushToken: tok.value,
                    deviceId: getOrCreateDeviceId(),
                });
            } catch {
                /* ignore */
            }
        }).then((h) => { regListener = h; });

        void PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
            const raw = event.notification?.data?.deepLink;
            if (typeof raw === "string") navigate(normalizeCapacitorDeepLink(raw));
        }).then((h) => { tapListener = h; });

        void runPushSetup();

        const onSettingsChange = () => void runPushSetup();
        window.addEventListener(PUSH_SETTINGS_EVENT, onSettingsChange);

        return () => {
            cancelled = true;
            window.removeEventListener(PUSH_SETTINGS_EVENT, onSettingsChange);
            void regListener?.remove();
            void tapListener?.remove();
        };
    }, [navigate, profile.token]);
}
